import React, { useCallback, useState, useEffect } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { useQuery, useQueryClient } from "react-query";
import Constants from "../services/Constants";
import ExportOption from "./common/ExportOption";
import { Typography, IconButton, Tooltip, Box } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { GoToPageControls } from "./common/GoToPageToolbar";

function CustomToolbar({
  onRefresh,
  exportCSV,
  exportProps,
  endpointFn,
  queryName,
  tableTitle,
  defaultExport,
  onClearFilters,
  hasFilter,
  totalPages,
  onPageChange,
}) {

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleToolbarRefresh = () => {
    setIsRefreshing(true);
    onRefresh();
    setTimeout(() => setIsRefreshing(false), 2000); 
  };

  return (
    <GridToolbarContainer
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0.75em 1.25em",
        borderBottom: "1px solid #e0e0e0",
        height: "4.2em",
      }}
    >
      <Typography variant="h6" style={{ fontWeight: 600, fontSize: "1rem" }}>
        {tableTitle}
      </Typography>
      {/* <div style={{ display: "flex", alignItems: "center", gap: "0.25em" }}> */}
       <Box
        display="flex"
        alignItems="center"
        gap={1}
        flexShrink={0}
        sx={{ width: "40%", justifyContent: "flex-end" }}
      >
        <GoToPageControls totalPages={totalPages} onPageChange={onPageChange} />
        <Tooltip title="Clear Filters" placement="top">
          <IconButton size="small" onClick={onClearFilters} disabled={!hasFilter}>
            <FilterAltOffIcon fontSize="small" color={hasFilter ? "primary" : "disabled"} />
          </IconButton>
        </Tooltip>
         <Tooltip title={isRefreshing ? "Refreshing..." : "Refresh"} placement="top">
          <span> 
            <IconButton size="small" onClick={handleToolbarRefresh} disabled={isRefreshing}>
              <RefreshIcon
                fontSize="small"
                color={isRefreshing ? "disabled" : "primary"}
              />
            </IconButton>
          </span>
        </Tooltip>
        {exportCSV ? (
          <ExportOption
            data={exportProps}
            endpointFn={endpointFn}
            queryName={queryName}
          />
        ) : (
          <>
            {defaultExport ? (
              <GridToolbarExport
                printOptions={{ disableToolbarButton: true }}
              />
            ) : (
              <div />
            )}
          </>
        )}
        </Box>
      {/* </div> */}
    </GridToolbarContainer>
  );
}

const OTAPagination = (props) => {
  const {
    endpointFn,
    queryName,
    sortByDefault,
    columns,
    extraFilters,
    exportRowsData,
    allVehicles = false,
    fetchAll = false,
    exportCSV = false,
    tableTitle,
    defaultExport = false,
    onSuccess,
    filters,
    extraQueriesToInvalidate = [],
    resetFilters = 0,
    onFilterApplied,
    onFiltersCleared,
    refetchInterval = 50000,
  } = props;

  const queryClient = useQueryClient();

  const [data, setData] = useState({
    loading: true,
    rows: [],
    totalRows: 0,
    rowCount: 0,
    pageSize: Constants.PAGE_SIZE,
    page: 1,
    field: sortByDefault,
    sort: "desc",
    filterModel: { items: [] },
  });

  const { filterModel = { items: [] }, page, pageSize, field, sort, rowCount } = data;

  useEffect(() => {
    if (filters !== undefined) {
      setData(prev => ({ ...prev, filterModel: { items: [...filters] } }));
    }
  }, [filters]);

  useEffect(() => {
    setData(prev => ({ ...prev, filterModel: { items: [] } }));
  }, [resetFilters]);

  const hasFilter = filterModel?.items?.some(
    item => item.value !== undefined && item.value !== ""
  );

  const queryKey = [
    queryName,
    page,
    pageSize,
    field,
    sort,
    filterModel,
    columns,
    extraFilters ? extraFilters : [],
    fetchAll,
    allVehicles,
  ];

  const { data: { data: { res: paginationData = [] } = {} } = {}, isLoading } =
    useQuery(queryKey, endpointFn, {
      refetchOnWindowFocus: false,
      refetchInterval,
      onSuccess: (response) => {
        const { data: { meta = {} } = {} } = response;
        setData({
          ...data,
          rowCount: meta.itemCount,
        });
        exportRowsData?.(response?.data?.res || []);
        onSuccess?.(response);
      },
    });

  const handleClearFilters = useCallback(() => {
    setData(prev => ({ ...prev, filterModel: { items: [] } }));
      if (onFiltersCleared) {
      onFiltersCleared();
    }
  }, [onFiltersCleared]);

  const handleSortModelChange = (newModel) => {
    let sortData = newModel[0] ?? { field: sortByDefault, sort: "desc" };
    setData({ ...data, ...sortData });
  };

  const onFilterChange = (filterModel) => {
    setData({
      ...data,
      filterModel,
    });
    if (onFilterApplied && filterModel.items && filterModel.items.length > 0) {
      onFilterApplied();
    }
    if (onFiltersCleared && (!filterModel.items || filterModel.items.length === 0)) {
      onFiltersCleared();
    }
  };

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries([queryName]);
    extraQueriesToInvalidate.forEach((query) => {
      queryClient.invalidateQueries([query]);
    });
  }, [queryName, queryClient, extraQueriesToInvalidate]);

  const totalPages = Math.ceil(rowCount / pageSize) || 1;

  const exportProps = [
    `${queryName}Export`,
    page,
    pageSize,
    field,
    sort,
    filterModel,
    columns,
    extraFilters ? extraFilters : [],
    true,
    allVehicles,
  ];

  return (
    <>
      <DataGrid
        initialState={{
          sorting: {
            sortModel: [{ field: sortByDefault, sort: "desc" }],
          },
        }}
        pageSize={pageSize}
        rowsPerPageOptions={Constants.ROWS_PER_PAGE_OPT}
        pagination
        sx={{ minHeight: 700 }}
        loading={isLoading}
        rowCount={rowCount}
        page={page - 1}
        rows={paginationData}
        onPageChange={(page) => {
          setData({
            ...data,
            page: page + 1,
          });
        }}
        onPageSizeChange={(pageSize) => {
          setData({
            ...data,
            page: 1,
            pageSize,
          });
        }}
        onSortModelChange={handleSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={onFilterChange}
        {...props}
        components={{
          ...props.components,
          Toolbar: CustomToolbar,
        }}
        componentsProps={{
          toolbar: {
            onRefresh: handleRefresh,
            exportCSV,
            exportProps,
            endpointFn,
            queryName,
            tableTitle,
            defaultExport,
            onClearFilters: handleClearFilters,
            hasFilter,
            totalPages,
            onPageChange: (parsed) => setData({ ...data, page: parsed }),
          },
        }}
      />
    </>
  );
};

/***
  The Following props are from the parent components and it differs

  columns
  onSelectionModelChange
  getRowId
  paginationMode
  checkboxSelection
***/

OTAPagination.defaultProps = {
  paginationMode: "server",
  sortByDefault: "createdAt",
  checkboxSelection: false,
  fetchAll: false,
  allVehicles: false,
  exportCSV: false,
  endpointFn: () => {},
  queryName: "",
  extraQueriesToInvalidate: [],
  resetFilters: 0,
};

export default OTAPagination;